import { enqueueTelemetryEvents, getTelemetryClientConfig } from '@ms/yammer-telemetry-store';

import type { InfoEvent, LogEvent, LogEventInput } from '../events';

export type DeliveryLifecycleEventInputType = 'Analytics' | 'AnalyticsV2' | 'Log';

export type DeliveryLifecycleOutcome =
  | 'attempted'
  | 'auth_unavailable'
  | 'fetch_failed'
  | 'invalid_client_id'
  | 'non_retryable_status'
  | 'pagehide_queue_observed'
  | 'sent'
  | 'retryable_status'
  | 'telemetry_host_unavailable';

export type DeliveryLifecycleErrorType =
  | 'AbortError'
  | 'Error'
  | 'NetworkError'
  | 'TimeoutError'
  | 'TypeError'
  | 'other';

export type PageHideDeliveryReason = 'skip_next_cleanup';
export type PageHideDeliveryTreatment = 'control' | 'treatment';

export const TelemetryDeliveryLifecycleEventName = 'telemetry_delivery_lifecycle';

type CreateDeliveryLifecycleCorrelationId = () => string;
export const createDeliveryLifecycleCorrelationId: CreateDeliveryLifecycleCorrelationId = () => crypto.randomUUID();

interface ReportDeliveryLifecycleInfoOptions {
  readonly apiCount?: number;
  readonly attempts?: number;
  readonly correlationId?: string;
  readonly encodedBodySize?: number;
  readonly errorType?: DeliveryLifecycleErrorType;
  readonly eventInputType: DeliveryLifecycleEventInputType;
  readonly outcome: DeliveryLifecycleOutcome;
  readonly persisted?: boolean;
  readonly reason?: PageHideDeliveryReason;
  readonly responseCode?: number;
  readonly resultCount?: number;
  readonly skipAuthentication?: boolean;
  readonly treatment?: PageHideDeliveryTreatment;
}

type IsDeliveryLifecycleEventName = (eventName: string) => boolean;
const isDeliveryLifecycleEventName: IsDeliveryLifecycleEventName = (eventName) =>
  eventName === TelemetryDeliveryLifecycleEventName;

type IsProductLogEvent = (event: LogEvent) => boolean;
export const isProductLogEvent: IsProductLogEvent = ({ name }) => !isDeliveryLifecycleEventName(name);

type IsDeliveryLifecycleLogEvent = (event: LogEventInput) => boolean;
export const isDeliveryLifecycleLogEvent: IsDeliveryLifecycleLogEvent = ({ EventName }) =>
  isDeliveryLifecycleEventName(EventName);

type HasDeliveryLifecycleLogEvent = (events: LogEventInput[]) => boolean;
export const hasDeliveryLifecycleLogEvent: HasDeliveryLifecycleLogEvent = (events) =>
  events.some(isDeliveryLifecycleLogEvent);

type GetReportableDeliveryLifecycleLogEventCount = (events: LogEventInput[]) => number;
export const getReportableDeliveryLifecycleLogEventCount: GetReportableDeliveryLifecycleLogEventCount = (events) =>
  events.filter((event) => !isDeliveryLifecycleLogEvent(event)).length;

type ShouldReportDeliveryLifecycleForLogEvents = (events: LogEventInput[]) => boolean;
export const shouldReportDeliveryLifecycleForLogEvents: ShouldReportDeliveryLifecycleForLogEvents = (events) =>
  getReportableDeliveryLifecycleLogEventCount(events) > 0;

export const getDeliveryLifecycleOutcomeForResponseStatus = (responseStatus: number): DeliveryLifecycleOutcome => {
  if (responseStatus === 408 || responseStatus === 429 || responseStatus >= 500) {
    return 'retryable_status';
  }

  return 'non_retryable_status';
};

type GetDeliveryLifecycleErrorType = (error: unknown) => DeliveryLifecycleErrorType;
export const getDeliveryLifecycleErrorType: GetDeliveryLifecycleErrorType = (error) => {
  const errorName = error instanceof Error ? error.name : undefined;

  switch (errorName) {
    case 'AbortError':
      return 'AbortError';
    case 'Error':
      return 'Error';
    case 'NetworkError':
      return 'NetworkError';
    case 'TimeoutError':
      return 'TimeoutError';
    case 'TypeError':
      return 'TypeError';
    default:
      return 'other';
  }
};

type CreateDeliveryLifecycleInfoEvent = (options: ReportDeliveryLifecycleInfoOptions) => InfoEvent;
export const createDeliveryLifecycleInfoEvent: CreateDeliveryLifecycleInfoEvent = ({
  apiCount,
  attempts,
  correlationId,
  encodedBodySize,
  errorType,
  eventInputType,
  outcome,
  persisted,
  reason,
  responseCode,
  resultCount,
  skipAuthentication,
  treatment,
}: ReportDeliveryLifecycleInfoOptions) => {
  const properties: InfoEvent['properties'] = {
    type: eventInputType,
    ...(attempts != null && { attempts }),
    ...(correlationId && { correlationId }),
    ...(treatment != null &&
      apiCount != null &&
      encodedBodySize != null && {
        data: JSON.stringify({
          treatment,
          apiCount,
          encodedBodySize,
          ...(persisted != null && { persisted }),
        }),
      }),
    outcome,
    ...(errorType && { errorType }),
    ...(reason && { reason }),
    ...(responseCode != null && { responseCode }),
    ...(resultCount != null && { resultCount }),
  };

  return {
    type: 'Info',
    name: TelemetryDeliveryLifecycleEventName,
    occurredAt: Date.now().toString(),
    skipAuthentication,
    properties,
  };
};

type ReportDeliveryLifecycleInfo = (options: ReportDeliveryLifecycleInfoOptions) => void;
export const reportDeliveryLifecycleInfo: ReportDeliveryLifecycleInfo = (options) => {
  if (!getTelemetryClientConfig()) {
    return;
  }

  enqueueTelemetryEvents<InfoEvent>('Info', [createDeliveryLifecycleInfoEvent(options)]);
};
